<template>
  <div class="role-management-container">
    <el-card class="box-card">
      <template #header>
        <div class="card-header">
          <span class="header-title">{{ $t("message.role.title") }}</span>
          <el-button type="primary" @click="handleAdd">{{ $t("message.role.add") }}</el-button>
        </div>
      </template>

      <div class="right-content">
        <!-- Search Form -->
        <div class="search-area">
          <el-form :inline="true">
            <el-form-item :label="$t('message.role.name')">
              <el-input
                v-model="queryParams.name"
                :placeholder="$t('message.role.inputName')"
                clearable
                @clear="handleQuery"
                @keyup.enter="handleQuery"
                style="width: 200px"
              />
            </el-form-item>
            <el-form-item>
              <el-button type="primary" @click="handleQuery">{{
                $t("message.common.search")
              }}</el-button>
            </el-form-item>
          </el-form>
        </div>

        <!-- Data Table -->
        <div class="table-wrapper">
          <el-table
            :data="roleList"
            border
            v-loading="loading"
            style="width: 100%"
          >
            <el-table-column
              prop="id"
              :label="$t('message.role.id')"
              width="80"
            />
            <el-table-column
              prop="name"
              :label="$t('message.role.name')"
              width="160"
              sortable
            />            
            <el-table-column
              prop="description"
              :label="$t('message.role.description')"
              min-width="150"
            />
            <el-table-column
              prop="user_count"
              :label="$t('message.role.userCount')"
              width="120"
              align="center"
            >
              <template #default="{ row }">
                <el-tag type="info">{{ row.user_count || 0 }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column
              :label="$t('message.role.actions')"
              width="200"
              fixed="right"
            >
              <template #default="{ row }">
                <el-button
                  link
                  type="primary"
                  size="small"
                  @click="handleEdit(row)"
                >
                  {{ $t("message.common.edit") }}
                </el-button>
                <el-button
                  link
                  type="primary"
                  size="small"
                  @click="handleMenuConfig(row)"
                >
                  {{ $t("message.role.menuConfig") }}
                </el-button>
                <el-button
                  link
                  type="danger"
                  size="small"
                  @click="handleDelete(row)"
                >
                  {{ $t("message.common.delete") }}
                </el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <div class="pagination-container">
          <el-pagination
            v-model:current-page="queryParams.pageNum"
            v-model:page-size="queryParams.pageSize"
            :total="total"
            :page-sizes="[5, 10, 15, 20, 30]"
            :layout="'total, sizes, prev, pager, next, jumper'"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
          />
        </div>
      </div>
    </el-card>

    <!-- 新增/编辑角色对话框 -->
    <el-dialog
      v-model="dialogVisible"
      :title="dialogTitle"
      width="500px"
      @closed="resetForm"
    >
      <el-form
        ref="formRef"
        :model="formData"
        :rules="rules"
        label-width="100px"
      >
        <el-form-item :label="$t('message.role.name')" prop="name">
          <el-input v-model="formData.name" :placeholder="$t('message.role.inputName')" />
        </el-form-item>

        <el-form-item :label="$t('message.role.nameEn')" prop="name_en">
          <el-input v-model="formData.name_en" :placeholder="$t('message.role.inputNameEn')" />
        </el-form-item>

        <el-form-item :label="$t('message.role.description')" prop="description">
          <el-input
            v-model="formData.description"
            type="textarea"
            :rows="3"
            :placeholder="$t('message.role.inputDescription')"
          />
        </el-form-item>
      </el-form>

      <template #footer>
        <span class="dialog-footer">
          <el-button @click="dialogVisible = false">{{ $t("message.common.cancel") }}</el-button>
          <el-button type="primary" @click="submitForm">{{ $t("message.common.confirm") }}</el-button>
        </span>
      </template>
    </el-dialog>

    <!-- 菜单权限配置对话框 -->
    <el-dialog
      v-model="menuDialogVisible"
      :title="$t('message.role.menuConfig')"
      width="600px"
      @closed="resetMenuForm"
    >
      <div class="menu-config-container">
        <el-tree
          ref="menuTreeRef"
          :data="menuTreeData"
          show-checkbox
          node-key="id"
          :props="menuTreeProps"
          default-expand-all
          :check-strictly="false"
        />
      </div>

      <template #footer>
        <span class="dialog-footer">
          <el-button @click="menuDialogVisible = false">{{ $t("message.common.cancel") }}</el-button>
          <el-button type="primary" @click="submitMenuConfig">{{ $t("message.common.save") }}</el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from "vue";
import { ElMessage, ElMessageBox } from "element-plus";
import { useI18n } from "vue-i18n";
import {
  getRoleList,
  getRoleById,
  addRole,
  updateRole,
  deleteRole,
  getRoleMenus,
  updateRoleMenus,
  getAllMenus,
} from "@/api/RoleManager";

const { t } = useI18n();

// ===== 查询参数 =====
const queryParams = ref({
  pageNum: 1,
  pageSize: 10,
  name: "",
});

// ===== 表格数据 =====
const roleList = ref([]);
const loading = ref(false);
const total = ref(0);

// ===== 表单数据 =====
const dialogVisible = ref(false);
const dialogTitle = ref("");
const formRef = ref();
const formData = reactive({
  id: 0,
  name: "",
  name_en: "",
  description: "",
});

// ===== 表单验证规则 =====
const rules = reactive({
  name: [
    {
      required: true,
      message: t("message.role.nameRequired"),
      trigger: "blur",
    },
    {
      min: 2,
      max: 30,
      message: t("message.role.nameLength"),
      trigger: "blur",
    },
  ],
  name_en: [
    {
      max: 60,
      message: t("message.role.nameEnLength"),
      trigger: "blur",
    },
  ],
  description: [
    {
      max: 100,
      message: t("message.role.descriptionLength"),
      trigger: "blur",
    },
  ],
});

// ===== 菜单权限配置 =====
const menuDialogVisible = ref(false);
const menuTreeRef = ref();
const menuTreeData = ref([]);
const currentRoleId = ref(0);
const menuTreeProps = {
  value: "id",
  label: "name",
  children: "children",
};

// ===== 获取角色列表 =====
const fetchData = async () => {
  if (loading.value) return;
  loading.value = true;
  try {
    const params = {
      pageNum: queryParams.value.pageNum,
      pageSize: queryParams.value.pageSize,
      ...(queryParams.value.name && { name: queryParams.value.name }), // 只有name有值时才包含
      ...(queryParams.value.companyId && {
        companyId: queryParams.value.companyId,
      }), // 只有companyId有值时才包含
    };

    const res = await getRoleList(params);
    if (res?.success) {
      roleList.value = res.data.list || [];
      total.value = res.data.totalCount || 0;
    } else {
      ElMessage.warning(res?.msg || t("message.role.loadFailed"));
    }
  } catch (error) {
    console.error("获取角色列表失败:", error);
    ElMessage.error(t("message.role.loadFailed"));
  } finally {
    loading.value = false;
  }
};

// ===== 查询 =====
const handleQuery = () => {
  queryParams.value.pageNum = 1;
  fetchData();
};

// ===== 分页 =====
const handleSizeChange = (val) => {
  queryParams.value.pageNum = 1;
  queryParams.value.pageSize = val;
  fetchData();
};

const handleCurrentChange = (val) => {
  queryParams.value.pageNum = val;
  fetchData();
};

// ===== 新增角色 =====
const handleAdd = () => {
  dialogTitle.value = t("message.role.add");
  formData.id = 0;
  formData.name = "";
  formData.name_en = "";
  formData.description = "";
  dialogVisible.value = true;
};

// ===== 编辑角色 =====
const handleEdit = async (row) => {
  dialogTitle.value = t("message.role.edit");
  try {
    const res = await getRoleById(row.id);
    if (res?.success) {
      const data = res.data;
      formData.id = data.id;
      formData.name = data.name;
      formData.name_en = data.name_en || "";
      formData.description = data.description || "";
      dialogVisible.value = true;
    } else {
      ElMessage.error(res?.msg || t("message.role.loadFailed"));
    }
  } catch (error) {
    console.error("获取角色详情失败:", error);
    ElMessage.error(t("message.role.loadFailed"));
  }
};

// ===== 删除角色 =====
const handleDelete = async (row) => {
  try {
    await ElMessageBox.confirm(
      t("message.role.deleteConfirm", { name: row.name }),
      t("message.common.warning"),
      {
        confirmButtonText: t("message.common.confirm"),
        cancelButtonText: t("message.common.cancel"),
        type: "warning",
      }
    );

    loading.value = true;
    const res = await deleteRole(row.id);
    if (res?.success) {
      ElMessage.success(t("message.role.deleteSuccess"));
      await fetchData();
    } else {
      ElMessage.error(res?.msg || t("message.role.deleteFailed"));
    }
  } catch (error) {
    if (error !== "cancel") {
      console.error("删除角色失败:", error);
      ElMessage.error(t("message.role.deleteFailed"));
    }
  } finally {
    loading.value = false;
  }
};

// ===== 提交表单 =====
const submitForm = async () => {
  try {
    await formRef.value?.validate();

    loading.value = true;
    const requestData = {
      id: formData.id,
      name: formData.name,
      name_en: formData.name_en,
      description: formData.description,
    };

    let res;
    if (formData.id > 0) {
      res = await updateRole(requestData);
    } else {
      res = await addRole(requestData);
    }

    if (res?.success) {
      ElMessage.success(formData.id > 0 ? t("message.role.updateSuccess") : t("message.role.addSuccess"));
      dialogVisible.value = false;
      await fetchData();
    } else {
      ElMessage.error(res?.msg || t("message.role.submitFailed"));
    }
  } catch (error) {
    console.error("提交表单失败:", error);
    ElMessage.error(t("message.role.submitFailed"));
  } finally {
    loading.value = false;
  }
};

// ===== 重置表单 =====
const resetForm = () => {
  formData.id = 0;
  formData.name = "";
  formData.name_en = "";
  formData.description = "";
  formRef.value?.clearValidate();
};

// ===== 菜单权限配置 =====
const handleMenuConfig = async (row) => {
  currentRoleId.value = row.id;
  menuDialogVisible.value = true;
  
  try {
    // 加载所有菜单
    const menuRes = await getAllMenus();
    if (menuRes?.success) {
      menuTreeData.value = buildMenuTree(menuRes.data || []);
    } else {
      ElMessage.error(menuRes?.msg || t("message.role.loadMenuFailed"));
    }

    // 加载角色已有菜单权限
    const roleMenuRes = await getRoleMenus(row.id);
    if (roleMenuRes?.success) {
      const checkedKeys = roleMenuRes.data || [];
      // 设置树选中状态
      menuTreeRef.value?.setCheckedKeys(checkedKeys);
    }
  } catch (error) {
    console.error("加载菜单数据失败:", error);
    ElMessage.error(t("message.role.loadMenuFailed"));
  }
};

// ===== 构建菜单树 =====
const buildMenuTree = (data) => {
  const map = {};
  const roots = [];
  
  data.forEach(item => {
    map[item.id] = { ...item, children: [] };
  });
  
  data.forEach(item => {
    if (item.parent_id && map[item.parent_id]) {
      map[item.parent_id].children.push(map[item.id]);
    } else {
      roots.push(map[item.id]);
    }
  });
  
  return roots;
};

// ===== 提交菜单权限 =====
const submitMenuConfig = async () => {
  try {
    const checkedKeys = menuTreeRef.value?.getCheckedKeys() || [];
    const halfCheckedKeys = menuTreeRef.value?.getHalfCheckedKeys() || [];
    const allMenuIds = [...checkedKeys, ...halfCheckedKeys];
  
    console.error("Role_CurrentRoleId:", currentRoleId.value);
    const requestData = {
      roleId: currentRoleId.value,
      menuIds: allMenuIds,
    };

    loading.value = true;
    const res = await updateRoleMenus(requestData);
    if (res?.success) {
      ElMessage.success(t("message.role.menuConfigSuccess"));
      menuDialogVisible.value = false;
    } else {
      ElMessage.error(res?.msg || t("message.role.menuConfigFailed"));
    }
  } catch (error) {
    console.error("保存菜单权限失败:", error);
    ElMessage.error(t("message.role.menuConfigFailed"));
  } finally {
    loading.value = false;
  }
};

// ===== 重置菜单表单 =====
const resetMenuForm = () => {
  currentRoleId.value = 0;
  menuTreeData.value = [];
  menuTreeRef.value?.setCheckedKeys([]);
};

// ===== 初始化 =====
onMounted(() => {
  fetchData();
});
</script>

<style scoped lang="scss">
.role-management-container {
  padding: 20px;

  .box-card {
    border-radius: 4px;

    .card-header {
      display: flex;
      justify-content: space-between;
      align-items: center;

      .header-title {
        font-size: 16px;
        font-weight: bold;
      }
    }
  }

  .right-content {
    border: 1px solid #ebeef5;
    border-radius: 4px;
    padding: 15px;
    background-color: #fff;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);

    .search-area {
      margin-bottom: 20px;

      :deep(.el-form-item) {
        margin-bottom: 0;
      }
    }

    .table-wrapper {
      width: 100%;
      margin-bottom: 20px;
      overflow-y: auto;

      :deep(.el-table) {
        width: 100% !important;
        border: 1px solid #ebeef5;
        border-radius: 4px;

        th,
        td {
          border-right: 1px solid #ebeef5;
          border-bottom: 1px solid #ebeef5;
        }
      }
    }

    .search-area,
    .pagination-container {
      flex-shrink: 0;
    }
  }

  .pagination-container {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin-top: 20px;
  }

  .menu-config-container {
    max-height: 500px;
    overflow-y: auto;
    
    :deep(.el-tree) {
      .el-tree-node__content {
        height: 34px;
      }
    }
  }
}
</style>